import { motion } from "framer-motion";
import { instruments } from "@/data/instruments";
import { ArrowUpRight } from "lucide-react";

export default function Products() {
  return (
    <section
      id="shop"
      className="relative py-28 md:py-40 px-6 md:px-12 lg:px-20 bg-cream"
      data-testid="products-section"
    >
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-[1px] bg-terracotta" />
              <span className="text-xs uppercase tracking-[0.3em] text-ink/60">
                La collection
              </span>
            </div>
            <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light text-ink leading-[0.95] max-w-2xl">
              Pièces <span className="italic-serif text-terracotta">emblématiques</span>
            </h2>
          </motion.div>
          <motion.a
            href="https://www.yogimarket.fr/category/all-products"
            target="_blank"
            rel="noreferrer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-ink hover:text-terracotta transition-colors group"
            data-testid="products-see-all"
          >
            Voir toute la boutique
            <ArrowUpRight
              size={16}
              className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
            />
          </motion.a>
        </div>

        {/* Products bento */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {instruments.map((p, i) => (
            <motion.a
              key={p.id}
              href={p.link}
              target="_blank"
              rel="noreferrer"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{
                duration: 0.8,
                delay: i * 0.08,
                ease: [0.16, 1, 0.3, 1],
              }}
              className={`group relative overflow-hidden rounded-3xl border border-ink/8 hover:border-terracotta/40 transition-all duration-500 ${
                i === 0 ? "md:col-span-2 md:row-span-2" : ""
              }`}
              style={{ backgroundColor: i === 0 ? "#EDE5D4" : "#E8E1D5" }}
              data-testid={`product-card-${p.id}`}
            >
              <div className="p-6 md:p-8 h-full flex flex-col">
                <div className="flex items-start justify-between mb-4">
                  <span
                    className="text-[10px] uppercase tracking-[0.25em]"
                    style={{ color: p.accent }}
                  >
                    {p.label}
                  </span>
                  <ArrowUpRight
                    size={20}
                    className="text-ink/30 group-hover:text-terracotta group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
                  />
                </div>

                <div
                  className={`relative flex-1 flex items-center justify-center ${
                    i === 0 ? "min-h-[400px]" : "min-h-[220px]"
                  }`}
                >
                  <div
                    className={`relative overflow-hidden rounded-full flex items-center justify-center ${
                      i === 0 ? "w-[85%] aspect-square" : "w-full aspect-square"
                    }`}
                    style={{
                      background: `radial-gradient(circle at 35% 30%, #F2ECE0 0%, ${p.accent}15 100%)`,
                    }}
                  >
                    <motion.img
                      src={p.image}
                      alt={p.name}
                      className="w-[120%] h-[120%] object-cover transition-transform duration-700 group-hover:scale-110"
                      style={{ mixBlendMode: "multiply" }}
                      whileHover={{ rotate: 8 }}
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <h3
                    className={`font-serif text-ink leading-tight mb-2 ${
                      i === 0 ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"
                    }`}
                  >
                    {p.name}
                  </h3>
                  <p className="text-xs text-ink/55 mb-4">{p.subtitle}</p>
                  <div className="flex items-baseline gap-2">
                    {p.priceOld && (
                      <span className="text-sm text-ink/40 line-through">
                        {p.priceOld}
                      </span>
                    )}
                    <span
                      className="font-serif text-xl"
                      style={{ color: p.accent }}
                    >
                      {p.price}
                    </span>
                  </div>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
